/**
 * Error Handler - Centralized error handling and user feedback system
 * Provides consistent error handling, logging, and user feedback across the application
 */

import { AppError, ErrorType, ErrorHandler as IErrorHandler } from '../types/validation';
import { ERROR_MESSAGES } from '../constants/validation-rules';

export class ErrorHandler implements IErrorHandler {
  private static instance: ErrorHandler;
  private errorContainer: HTMLElement | null = null;
  private liveRegion: HTMLElement | null = null;

  private constructor() {
    this.initializeErrorContainer();
    this.initializeLiveRegion();
  }

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private initializeErrorContainer(): void {
    // Create global error container if it doesn't exist
    this.errorContainer = document.getElementById('global-error-container');
    if (!this.errorContainer) {
      this.errorContainer = document.createElement('div');
      this.errorContainer.id = 'global-error-container';
      this.errorContainer.className = 'error-container';
      this.errorContainer.setAttribute('role', 'alert');
      this.errorContainer.setAttribute('aria-live', 'assertive');
      this.errorContainer.style.display = 'none';
      document.body.appendChild(this.errorContainer);
    }
  }

  private initializeLiveRegion(): void {
    // Get or create live region for screen reader announcements
    this.liveRegion = document.getElementById('live-region');
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('div');
      this.liveRegion.id = 'live-region';
      this.liveRegion.setAttribute('aria-live', 'polite');
      this.liveRegion.setAttribute('aria-atomic', 'true');
      this.liveRegion.className = 'sr-only';
      document.body.appendChild(this.liveRegion);
    }
  }

  public handleError(error: AppError): void {
    // Log the error
    this.logError(error);
    
    // Display the error to the user
    this.displayError(error);
    
    // Announce to screen readers
    this.announceError(error);
  }

  public logError(error: AppError): void {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Application Error:', {
        type: error.type,
        code: error.code,
        message: error.message,
        details: error.details,
        timestamp: error.timestamp,
        field: error.field
      });
    }

    // In production, you might want to send errors to a logging service
    // this.sendToLoggingService(error);
  }

  public displayError(error: AppError): void {
    if (!this.errorContainer) return;

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'error-message';
    errorElement.setAttribute('role', 'alert');
    
    // Create error content
    const errorContent = this.createErrorContent(error);
    errorElement.appendChild(errorContent);
    
    // Clear previous errors and add new one
    this.errorContainer.innerHTML = '';
    this.errorContainer.appendChild(errorElement);
    this.errorContainer.style.display = 'block';
    
    // Auto-hide non-critical errors after 5 seconds
    if (error.type !== 'validation') {
      setTimeout(() => {
        this.clearError();
      }, 5000);
    }
  }

  private createErrorContent(error: AppError): HTMLElement {
    const content = document.createElement('div');
    content.className = 'error-content';
    
    // Error icon
    const icon = document.createElement('span');
    icon.className = 'error-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = this.getErrorIcon(error.type);
    
    // Error message
    const message = document.createElement('span');
    message.className = 'error-text';
    message.textContent = error.message;
    
    // Close button
    const closeButton = document.createElement('button');
    closeButton.className = 'error-close';
    closeButton.setAttribute('aria-label', 'Fermer le message d\'erreur');
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', () => this.clearError());
    
    content.appendChild(icon);
    content.appendChild(message);
    content.appendChild(closeButton);
    
    return content;
  }

  private getErrorIcon(type: ErrorType): string {
    const icons: Record<ErrorType, string> = {
      validation: '⚠️',
      network: '🌐',
      server: '🔧',
      client: '💻',
      not_found: '🔍',
      unauthorized: '🔒',
      forbidden: '🚫'
    };
    return icons[type] || '❌';
  }

  private announceError(error: AppError): void {
    if (!this.liveRegion) return;
    
    // Announce error to screen readers
    this.liveRegion.textContent = `Erreur: ${error.message}`;
    
    // Clear announcement after a delay
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = '';
      }
    }, 3000);
  }

  public clearError(field?: string): void {
    if (field) {
      // Clear field-specific error
      const fieldError = document.querySelector(`#${field}-error`);
      if (fieldError) {
        fieldError.textContent = '';
        (fieldError as HTMLElement).style.display = 'none';
      }
      
      const fieldInput = document.querySelector(`#${field}`) as HTMLInputElement;
      if (fieldInput) {
        fieldInput.classList.remove('error');
        fieldInput.removeAttribute('aria-invalid');
      }
    } else {
      // Clear global error
      if (this.errorContainer) {
        this.errorContainer.style.display = 'none';
        this.errorContainer.innerHTML = '';
      }
    }
  }

  // Factory methods for creating common errors
  public static createValidationError(field: string, message: string): AppError {
    return {
      type: 'validation',
      code: 'VALIDATION_ERROR',
      message,
      field,
      timestamp: new Date()
    };
  }

  public static createNetworkError(message?: string): AppError {
    return {
      type: 'network',
      code: 'NETWORK_ERROR',
      message: message || ERROR_MESSAGES.NETWORK_ERROR,
      timestamp: new Date()
    };
  }

  public static createServerError(message?: string): AppError {
    return {
      type: 'server',
      code: 'SERVER_ERROR',
      message: message || ERROR_MESSAGES.SERVER_ERROR,
      timestamp: new Date()
    };
  }

  public static createNotFoundError(message?: string): AppError {
    return {
      type: 'not_found',
      code: 'NOT_FOUND',
      message: message || ERROR_MESSAGES.NOT_FOUND,
      timestamp: new Date()
    };
  }

  // Utility method to handle fetch errors
  public static async handleFetchError(response: Response): Promise<AppError> {
    let message: string;
    
    switch (response.status) {
      case 404:
        message = ERROR_MESSAGES.NOT_FOUND;
        break;
      case 401:
        message = ERROR_MESSAGES.UNAUTHORIZED;
        break;
      case 403:
        message = ERROR_MESSAGES.FORBIDDEN;
        break;
      case 500:
        message = ERROR_MESSAGES.SERVER_ERROR;
        break;
      default:
        message = ERROR_MESSAGES.GENERIC_ERROR;
    }

    return {
      type: response.status >= 500 ? 'server' : 'client',
      code: `HTTP_${response.status}`,
      message,
      details: {
        status: response.status,
        statusText: response.statusText,
        url: response.url
      },
      timestamp: new Date()
    };
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();